import { lazy, Suspense, useEffect, useState } from "react";
import "./index.css";
import { AiUsage } from "./components/AiUsage";
import { ConfigBanner } from "./components/ConfigBanner";
import { ConfigEditor } from "./components/ConfigEditor";
import { CustomFieldMappings } from "./components/CustomFieldMappings";
import { DevAuthPicker } from "./components/DevAuthPicker";
import { DevAuthStatus } from "./components/DevAuthStatus";
import { ExtensionStores } from "./components/ExtensionStores";
import { FieldsExplorer } from "./components/FieldsExplorer";
import { FiltersEditor } from "./components/FiltersEditor";
import { LlmPromptEditor } from "./components/LlmPromptEditor";
import { LockedFields } from "./components/LockedFields";
import { MappingEditor } from "./components/MappingEditor";
import { SalesforceStatus } from "./components/SalesforceStatus";
import { SoqlConsole } from "./components/SoqlConsole";
import { SubscriberConfigCopy } from "./components/SubscriberConfigCopy";
import { SubscriberPicker } from "./components/SubscriberPicker";
import { getHealth } from "./lib/client";
import type { HealthResponse } from "./lib/types";

type Tab =
  | "connection"
  | "filters"
  | "mappings"
  | "custom-fields"
  | "fields"
  | "soql"
  | "graphql"
  | "llm-prompt"
  | "locked-fields"
  | "config-copy"
  | "extension-stores"
  | "ai-usage";

const TABS: { id: Tab; label: string }[] = [
  { id: "connection", label: "Connection" },
  { id: "filters", label: "Filters" },
  { id: "mappings", label: "Mappings" },
  { id: "custom-fields", label: "Custom fields" },
  { id: "fields", label: "Fields" },
  { id: "soql", label: "SOQL" },
  { id: "graphql", label: "GraphQL" },
  { id: "llm-prompt", label: "LLM prompt" },
  { id: "locked-fields", label: "Locked fields" },
  { id: "config-copy", label: "Config copy" },
  { id: "extension-stores", label: "Extension stores" },
  { id: "ai-usage", label: "AI usage" },
];

// GraphiQL brings the Monaco editor with it, which is most of this app's
// bundle. Load it only when someone opens the tab.
const GraphqlConsole = lazy(() =>
  import("./components/GraphqlConsole").then((m) => ({
    default: m.GraphqlConsole,
  })),
);

/** The tab named by the URL's anchor, e.g. `#ai-usage`. Anything else opens the first tab. */
function tabFromHash(): Tab {
  const id = window.location.hash.slice(1);
  return TABS.find((t) => t.id === id)?.id ?? "connection";
}

const STORAGE_KEY_ID = "ic.subscriberId";
const STORAGE_KEY_NAME = "ic.subscriberName";

/**
 * Click-to-copy row for the selected subscriber's ids. The picker shows a
 * name, but ps-main tooling and support tickets want an id, so both forms
 * stay one click away.
 *
 * ps-main encodes a Relay Global ID as base64("Subscriber:<uuid>"). See
 * `decodeGlobalId` in lib/subscribers.ts, which unwraps the same form. The
 * id is derived here, so a hand-pasted UUID gets a Global ID too.
 */
function SubscriberIds({ id }: { id: string }) {
  const [copied, setCopied] = useState<{ label: string; ok: boolean } | null>(
    null,
  );

  async function copy(label: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ label, ok: true });
    } catch {
      setCopied({ label, ok: false });
    }
    setTimeout(() => setCopied(null), 1500);
  }

  const ids = [
    ["UUID", id],
    ["Global ID", btoa(`Subscriber:${id}`)],
  ] as const;

  return (
    <div className="subscriber-ids">
      {ids.map(([label, text]) => (
        <button
          key={label}
          type="button"
          className="id-chip"
          title={`Copy ${label}`}
          onClick={() => copy(label, text)}
        >
          <span className="id-chip-label">{label}</span>
          <code>{text}</code>
          {copied?.label === label && (
            <span className={copied.ok ? "id-chip-copied" : "id-chip-failed"}>
              {copied.ok ? "copied" : "copy failed"}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function App() {
  const [subscriberId, setSubscriberId] = useState(
    () => localStorage.getItem(STORAGE_KEY_ID) ?? "",
  );
  const [subscriberName, setSubscriberName] = useState(
    () => localStorage.getItem(STORAGE_KEY_NAME) ?? "",
  );
  const [tab, setTab] = useState<Tab>(tabFromHash);
  const [statusVersion, setStatusVersion] = useState(0);
  const [health, setHealth] = useState<HealthResponse | null>(null);

  // Each tab is a link to its own anchor, so the hash is the source of truth:
  // a tab can be bookmarked or shared, and back and forward move between tabs.
  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    getHealth()
      .then(setHealth)
      .catch(() =>
        setHealth({
          ok: false,
          configured: false,
          assistantEnabled: false,
          devAuthEnabled: false,
          devAuthUserId: null,
        }),
      );
  }, []);

  useEffect(() => {
    if (subscriberId) localStorage.setItem(STORAGE_KEY_ID, subscriberId);
    else localStorage.removeItem(STORAGE_KEY_ID);
  }, [subscriberId]);

  useEffect(() => {
    if (subscriberName) localStorage.setItem(STORAGE_KEY_NAME, subscriberName);
    else localStorage.removeItem(STORAGE_KEY_NAME);
  }, [subscriberName]);

  const trimmed = subscriberId.trim();
  const valid = Boolean(trimmed);

  // Wait for the health check before deciding whether to show the dev-auth
  // picker — avoids flashing the normal app first.
  if (!health) return null;

  if (health.devAuthEnabled && !health.devAuthUserId) {
    return <DevAuthPicker />;
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">⚙</span>
          <div>
            <h1>Mechanicus</h1>
            <p className="subtitle">
              Salesforce config admin · ps-integrations
            </p>
          </div>
        </div>
        <div className="subscriber-bar">
          <SubscriberPicker
            value={subscriberName || subscriberId}
            onResolve={(id, name) => {
              setSubscriberId(id);
              setSubscriberName(name);
            }}
          />
          {valid && <SubscriberIds id={trimmed} />}
        </div>
      </header>

      {health.devAuthEnabled && health.devAuthUserId && (
        <DevAuthStatus userId={health.devAuthUserId} />
      )}

      <ConfigBanner configured={health.configured} />

      {valid && (
        <SalesforceStatus subscriberId={trimmed} refreshKey={statusVersion} />
      )}

      <nav className="tabs">
        {TABS.map((t) => (
          <a
            key={t.id}
            href={`#${t.id}`}
            className={`tab${tab === t.id ? " active" : ""}`}
            aria-current={tab === t.id ? "page" : undefined}
          >
            {t.label}
          </a>
        ))}
      </nav>

      <main className="content">
        {tab === "graphql" ? (
          <Suspense
            fallback={<div className="placeholder">Loading GraphiQL…</div>}
          >
            <GraphqlConsole />
          </Suspense>
        ) : tab === "extension-stores" ? (
          // No subscriber needed. Support looks a user up wherever they are.
          <ExtensionStores subscriberId={trimmed} />
        ) : tab === "ai-usage" ? (
          // Spans every subscriber. A selected one opens its breakdown.
          <AiUsage subscriberId={trimmed} />
        ) : !valid ? (
          <div className="placeholder">
            Search for a subscriber above to begin.
          </div>
        ) : tab === "connection" ? (
          <ConfigEditor
            subscriberId={trimmed}
            onSaved={() => setStatusVersion((v) => v + 1)}
          />
        ) : tab === "filters" ? (
          <FiltersEditor subscriberId={trimmed} />
        ) : tab === "mappings" ? (
          <MappingEditor subscriberId={trimmed} />
        ) : tab === "custom-fields" ? (
          <CustomFieldMappings subscriberId={trimmed} />
        ) : tab === "fields" ? (
          <FieldsExplorer subscriberId={trimmed} />
        ) : tab === "llm-prompt" ? (
          <LlmPromptEditor subscriberId={trimmed} />
        ) : tab === "locked-fields" ? (
          <LockedFields subscriberId={trimmed} />
        ) : tab === "config-copy" ? (
          <SubscriberConfigCopy
            subscriberId={trimmed}
            subscriberName={subscriberName}
          />
        ) : (
          <SoqlConsole subscriberId={trimmed} subscriberName={subscriberName} />
        )}
      </main>
    </div>
  );
}

export default App;
